import { useMemo } from "react";
import { useScopedWhere } from "#app/widgets/dashboards/scope/use-scoped-where";
import { useWidgetFilters } from "#app/widgets/dashboards/widget-filters";
import { useIssueGroupCounts } from "#app/hooks/issue-list/use-issue-group-counts";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { combineWhereConditions } from "#app/lib/query-utils";
import type { StageCategory } from "./category";

/** Counts the Projects in scope whose Stage is of ONE category —
 *  Completed or Canceled, never both: a figure over the pair had no honest
 *  caption, which is how "closed" once leaked onto the tile. */
export function useProjectsClosed(category: StageCategory, filters: unknown) {
  const { stages } = useWorkspaceData();
  const stageIds = useMemo(
    () => stages.filter((s) => s.type === category).map((s) => s.stageId),
    [stages, category],
  );
  const { base, override } = useWidgetFilters(filters);
  const mergedBase = useMemo(
    () =>
      stageIds.length > 0
        ? combineWhereConditions([base, { stageId: { in: stageIds } }]) ?? {}
        : undefined,
    [base, stageIds],
  );
  const { where } = useScopedWhere(mergedBase, { override });
  const effectiveWhere = stageIds.length > 0 ? where : undefined;
  const { counts, ready, error } = useIssueGroupCounts({
    where: effectiveWhere,
    groupBy: stageIds.length > 0 ? "stageId" : undefined,
  });

  const value = useMemo(
    () => stageIds.reduce((sum, id) => sum + (counts[id] ?? 0), 0),
    [counts, stageIds],
  );

  if (stageIds.length === 0) return { value: 0, stageIds, loading: false, error };
  return { value, stageIds, loading: !ready, error };
}
